import useEvent from "react-use-event-hook";
import { useRecoilCallback } from "recoil";

import {
  editFormDataAF,
  editFormDataInitialSnapshotAF,
  isValidPositiveNumber,
  RideCrudModeKeyTuple,
} from "@ryde/shared";

/**
 * Clears cached ride edit DTO when list/kanban mutations change ride data out-of-band.
 * Next open via Link refetches fresh data from API instead of skipping fetch (same id in Recoil).
 */
export const useClearStaleRideEditDto = () => {
  const clearStaleRideEditDtoCache = useRecoilCallback(
    ({ set, snapshot }) =>
      (affectedRideIds: number[]) => {
        const affectedIdSet = new Set(
          affectedRideIds.filter(isValidPositiveNumber),
        );

        if (!affectedIdSet.size) {
          return;
        }

        const currentDto = snapshot
          .getLoadable(editFormDataAF(RideCrudModeKeyTuple))
          .getValue();

        const currentRideId = currentDto?.id;

        if (
          !isValidPositiveNumber(currentRideId) ||
          !affectedIdSet.has(currentRideId)
        ) {
          return;
        }

        set(editFormDataAF(RideCrudModeKeyTuple), undefined);
        set(editFormDataInitialSnapshotAF(RideCrudModeKeyTuple), undefined);
      },
    [],
  );

  const clearStaleRideEditDtoCacheForRide = useEvent((rideId: number) => {
    if (!isValidPositiveNumber(rideId)) {
      return;
    }

    clearStaleRideEditDtoCache([rideId]);
  });

  return { clearStaleRideEditDtoCache, clearStaleRideEditDtoCacheForRide };
};
